/**
 * Per-tab memory of the active workspace.
 *
 * The active workspace is stored server-side in `user_active_organization`,
 * one row per user, because RLS reads it: get_current_organization_id() resolves
 * the caller's organization from that row. That makes it a single, global
 * pointer — so two tabs on different workspaces overwrite each other, and any
 * auth event (token refresh on tab focus, cross-tab auth sync) re-reads the row
 * and yanks the tab to whatever the other one last wrote.
 *
 * sessionStorage is scoped to a single tab and survives the reload that
 * switchWorkspace performs, so it is the right place to remember what *this*
 * tab was looking at. The server row still gets written — Realtime, edge
 * functions and cron resolve through it — but the tab, not the row, decides
 * what the tab shows, and every PostgREST request carries the tab's workspace
 * in the `x-active-org` header so RLS scopes that request to the same
 * workspace the tab is rendering. get_current_organization_id() honors the
 * header only for organizations the caller already belongs to, and falls back
 * to the row when it is absent.
 *
 * One caller needs to override that: the super-admin panel opens a workspace in
 * a new tab, and a tab opened with window.open() inherits a *copy* of the
 * opener's sessionStorage. Without an explicit signal the new tab would start
 * out remembering the admin's own workspace and stay there, ignoring the
 * impersonation the server just recorded. Hence the URL request below.
 */

const STORAGE_KEY = "flow.activeWorkspaceId";

/** Query param the super-admin panel uses to open a specific workspace. */
export const WORKSPACE_PARAM = "org";

export function workspaceEntryUrl(path: string, organizationId: string): string {
  return `${path}?${WORKSPACE_PARAM}=${encodeURIComponent(organizationId)}`;
}

/** Header that pins a PostgREST request to the workspace this tab is showing. */
export const ACTIVE_ORG_HEADER = "x-active-org";

const REST_PATH = "/rest/v1/";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** sessionStorage throws in some privacy modes; never let that break auth. */
export function readTabWorkspace(): string | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
}

export function writeTabWorkspace(organizationId: string | null): void {
  try {
    if (organizationId) sessionStorage.setItem(STORAGE_KEY, organizationId);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore — the server row remains the source of truth for RLS */
  }
}

/** Workspace explicitly requested for this tab via `?org=`, if any. */
export function readWorkspaceFromUrl(search?: string): string | null {
  try {
    const raw = new URLSearchParams(search ?? window.location.search).get(WORKSPACE_PARAM);
    return raw && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

/**
 * Drop `?org=` once it has been applied, so a later reload of this tab follows
 * the tab's own choice instead of replaying the original request.
 */
export function stripWorkspaceParamFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(WORKSPACE_PARAM)) return;
    url.searchParams.delete(WORKSPACE_PARAM);
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  } catch {
    /* ignore — the workspace is already resolved either way */
  }
}

/**
 * Workspace this tab should scope its requests to, before AuthContext has
 * resolved anything. The URL request comes first for the same reason it does in
 * resolveActiveWorkspace: a tab opened by the admin panel inherits the opener's
 * sessionStorage. Anything that is not a UUID is dropped rather than sent.
 */
export function readRequestWorkspace(): string | null {
  const candidate = readWorkspaceFromUrl() ?? readTabWorkspace();
  return candidate && UUID_PATTERN.test(candidate) ? candidate : null;
}

/**
 * Wraps fetch so PostgREST calls carry this tab's workspace. Scoped to
 * `/rest/v1/`: Storage and Edge Functions answer preflight with their own fixed
 * allow-list, and an unexpected header there would fail CORS.
 */
export function createActiveOrgFetch(baseFetch?: typeof fetch): typeof fetch {
  return (input, init) => {
    const send = baseFetch ?? globalThis.fetch;
    const url = typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;

    const activeOrg = url.includes(REST_PATH) ? readRequestWorkspace() : null;
    if (!activeOrg) return send(input, init);

    const isRequest = typeof Request !== "undefined" && input instanceof Request;
    const headers = new Headers(init?.headers ?? (isRequest ? (input as Request).headers : undefined));
    headers.set(ACTIVE_ORG_HEADER, activeOrg);

    return send(input, { ...init, headers });
  };
}

export type ResolveWorkspaceInput = {
  /** Workspace requested for this tab through the URL, e.g. super-admin impersonation. */
  urlRequest?: string | null;
  /** What this tab was last showing, from sessionStorage. */
  tabChoice: string | null;
  /** What `user_active_organization` currently holds for this user. */
  storedOrgId: string | null;
  /** Organizations this user may act in on the current brand. */
  allowedIds: readonly string[];
  /** First workspace, used when nothing else is usable. */
  fallbackId: string | null;
};

/**
 * Decide which workspace this tab should show.
 *
 * An explicit URL request wins over everything: it is a deliberate "open this
 * workspace here", and a tab opened by the admin panel inherits the opener's
 * sessionStorage, so the inherited value must not outrank it. Otherwise the
 * tab's own choice wins over the shared server row — that is the whole point:
 * another tab writing the row must not move this one. All three are checked
 * against `allowedIds` so a stale or cross-brand value can never be applied.
 */
export function resolveActiveWorkspace({
  urlRequest,
  tabChoice,
  storedOrgId,
  allowedIds,
  fallbackId,
}: ResolveWorkspaceInput): string | null {
  const allowed = new Set(allowedIds.filter(Boolean));

  if (urlRequest && allowed.has(urlRequest)) return urlRequest;
  if (tabChoice && allowed.has(tabChoice)) return tabChoice;
  if (storedOrgId && allowed.has(storedOrgId)) return storedOrgId;
  return fallbackId ?? null;
}
